import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import Spinner from '../components/Spinner';
import { FiCheck, FiX, FiUser, FiMail, FiEdit, FiSearch, FiFilter, FiTrash2, FiUsers, FiActivity, FiDollarSign, FiShield, FiEye, FiCalendar, FiPhone, FiMapPin } from 'react-icons/fi';
import ConfirmModal from './ConfirmModal';

const ChangePlanModal = ({ isOpen, onClose, onSave, currentPlan }) => {
    const [selectedPlan, setSelectedPlan] = useState(currentPlan || 'essentiel');

    useEffect(() => {
        if (isOpen) setSelectedPlan(currentPlan || 'essentiel');
    }, [isOpen, currentPlan]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden transform transition-all scale-100">
                <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Changer le plan</h3>
                    <div className="space-y-3">
                        {['essentiel', 'pro', 'premium'].map((plan) => (
                            <label key={plan} className={`flex items-center p-3 border rounded-xl cursor-pointer transition-all ${selectedPlan === plan ? 'border-brand-blue bg-blue-50 ring-1 ring-brand-blue' : 'border-gray-200 hover:bg-gray-50'}`}>
                                <input type="radio" name="plan" value={plan} checked={selectedPlan === plan} onChange={(e) => setSelectedPlan(e.target.value)} className="h-4 w-4 text-brand-blue focus:ring-brand-blue border-gray-300" />
                                <div className="ml-3">
                                    <span className="block text-sm font-medium text-gray-900 capitalize">{plan}</span>
                                </div>
                            </label>
                        ))}
                    </div>
                </div>
                <div className="bg-gray-50 px-4 py-4 sm:px-6 flex flex-col sm:flex-row-reverse gap-3">
                    <button type="button" className="w-full inline-flex justify-center items-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-brand-blue text-base font-medium text-white hover:bg-blue-700 focus:outline-none sm:w-auto sm:text-sm" onClick={() => onSave(selectedPlan)}>
                        Enregistrer
                    </button>
                    <button type="button" className="w-full inline-flex justify-center items-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:w-auto sm:text-sm" onClick={onClose}>
                        Annuler
                    </button>
                </div>
            </div>
        </div>
    );
};

const SubscriptionDateModal = ({ isOpen, onClose, onSave, user }) => {
    const [endDate, setEndDate] = useState('');
    const isActive = user?.subscription_status === 'active';

    useEffect(() => {
        if (isOpen && user) {
            let dateToSet = new Date();
            const currentExp = user.subscription_ends_at ? new Date(user.subscription_ends_at) : null;
            const now = new Date();

            if (isActive && currentExp && currentExp > now) {
                // Si actif et non expiré : on propose d'ajouter 1 mois à la date d'expiration actuelle
                dateToSet = new Date(currentExp);
                dateToSet.setMonth(dateToSet.getMonth() + 1);
            } else {
                // Sinon (inactif ou expiré) : on propose 1 mois à partir d'aujourd'hui
                dateToSet.setMonth(dateToSet.getMonth() + 1);
            }
            setEndDate(dateToSet.toISOString().split('T')[0]);
        }
    }, [isOpen, user, isActive]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden transform transition-all scale-100">
                <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">{isActive ? 'Renouveler l\'abonnement' : 'Activer l\'abonnement'}</h3>
                    <p className="text-sm text-gray-500 mb-4">
                        {isActive 
                            ? <>Nouvelle date d'expiration pour <strong>{user?.username}</strong></>
                            : <>Définir la date d'expiration pour <strong>{user?.username}</strong></>
                        }
                    </p>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date de fin</label>
                    <input 
                        type="date" 
                        value={endDate} 
                        onChange={(e) => setEndDate(e.target.value)} 
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-brand-blue outline-none"
                    />
                </div>
                <div className="bg-gray-50 px-4 py-4 sm:px-6 flex flex-col sm:flex-row-reverse gap-3">
                    <button type="button" disabled={!endDate} className="w-full inline-flex justify-center items-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed" onClick={() => onSave(user._id, endDate)}>
                        {isActive ? 'Confirmer le renouvellement' : 'Valider et Activer'}
                    </button>
                    <button type="button" className="w-full inline-flex justify-center items-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:w-auto sm:text-sm" onClick={onClose}>
                        Annuler
                    </button>
                </div>
            </div>
        </div>
    );
};

const UserDetailModal = ({ isOpen, onClose, user }) => {
    if (!isOpen || !user) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden transform transition-all scale-100">
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                    <h3 className="text-xl font-bold text-gray-800">Détails de l'utilisateur</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><FiX size={24} /></button>
                </div>
                <div className="p-6 space-y-4">
                    <div className="flex items-center space-x-4 mb-6">
                        <div className="h-16 w-16 rounded-full bg-brand-blue/10 text-brand-blue flex items-center justify-center font-bold text-2xl">
                            {user.username ? user.username.charAt(0).toUpperCase() : 'U'}
                        </div>
                        <div>
                            <h4 className="text-xl font-bold text-gray-900">{user.username}</h4>
                            <p className="text-gray-500">{user.email}</p>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${
                                user.role === 'admin' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
                            }`}>
                                {user.role || 'Utilisateur'}
                            </span>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="p-3 bg-gray-50 rounded-xl">
                            <p className="text-xs text-gray-500 uppercase font-bold mb-1">Boutique</p>
                            <p className="font-semibold text-gray-800 flex items-center truncate" title={user.shopName}><FiShield className="mr-2 text-gray-400"/> {user.shopName || 'N/A'}</p>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-xl">
                            <p className="text-xs text-gray-500 uppercase font-bold mb-1">Plan Actuel</p>
                            <p className="font-semibold text-gray-800 capitalize">{user.plan || 'Essentiel'}</p>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-xl">
                            <p className="text-xs text-gray-500 uppercase font-bold mb-1">Statut</p>
                            <p className={`font-semibold capitalize ${user.subscription_status === 'active' ? 'text-green-600' : 'text-red-600'}`}>
                                {user.subscription_status || 'Inactif'}
                            </p>
                            {user.subscription_ends_at && (
                                <p className="text-xs text-gray-500 mt-1">Exp: {new Date(user.subscription_ends_at).toLocaleDateString()}</p>
                            )}
                        </div>
                        <div className="p-3 bg-gray-50 rounded-xl">
                            <p className="text-xs text-gray-500 uppercase font-bold mb-1">Inscrit le</p>
                            <p className="font-semibold text-gray-800 flex items-center">
                                <FiCalendar className="mr-2 text-gray-400"/> 
                                {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                            </p>
                        </div>
                    </div>
                    
                    <div className="border-t border-gray-100 pt-4 space-y-2">
                        <div className="flex items-center text-gray-700">
                            <FiPhone className="mr-3 text-gray-400" /> {user.phone || 'Aucun téléphone'}
                        </div>
                        <div className="flex items-center text-gray-700">
                            <FiMapPin className="mr-3 text-gray-400" /> {user.address || 'Aucune adresse'}
                        </div>
                        <div className="text-xs text-gray-400 mt-2 font-mono">
                            ID: {user._id}
                        </div>
                    </div>
                </div>
                <div className="bg-gray-50 px-6 py-4 flex justify-end">
                    <button onClick={onClose} className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium shadow-sm">
                        Fermer
                    </button>
                </div>
            </div>
        </div>
    );
};

const AdminPanel = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterPlan, setFilterPlan] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
    const [userToEditPlan, setUserToEditPlan] = useState(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [userToView, setUserToView] = useState(null);
    const [isSubscriptionDateModalOpen, setIsSubscriptionDateModalOpen] = useState(false);
    const [userToActivate, setUserToActivate] = useState(null);

    useEffect(() => {
        const fetchUsers = async () => {
            setLoading(true);
            try {
                const response = await api.get('/admin/users');
                setUsers(response.data);
            } catch (error) {
                console.error("Erreur Admin:", error);
                if (error.response && error.response.status === 403) {
                    toast.warn("Mode Démo : Accès serveur refusé (403). Données fictives chargées.");
                    setUsers([
                        { _id: '1', username: 'Ndiaye', email: 'doucoure.papa10@gmail.com', plan: 'premium', subscription_status: 'active', role: 'admin', shopName: 'Chez Ndiaye' },
                        { _id: '2', username: 'Boutique Pro', email: 'pro@shop.com', plan: 'pro', subscription_status: 'active', role: 'owner', shopName: 'Superette Dakar' },
                        { _id: '3', username: 'Utilisateur Test', email: 'test@free.com', plan: 'essentiel', subscription_status: 'inactive', role: 'owner', shopName: 'Epicerie du Coin' },
                        { _id: '4', username: 'Fatou Diop', email: 'fatou@shop.sn', plan: 'premium', subscription_status: 'active', role: 'owner', shopName: 'Fatou Mode' },
                        { _id: '5', username: 'Moussa Fall', email: 'moussa@market.sn', plan: 'pro', subscription_status: 'trialing', role: 'owner', shopName: 'Market Place' }
                    ]);
                } else if (error.response && error.response.status === 404) {
                     toast.error("L'endpoint /admin/users n'existe pas (404).");
                } else {
                    toast.error("Impossible de charger la liste des utilisateurs.");
                }
            } finally {
                setLoading(false);
            }
        };

        fetchUsers();
    }, []);

    const handleOpenActivationModal = (user) => {
        setUserToActivate(user);
        setIsSubscriptionDateModalOpen(true);
    };

    const handleConfirmActivation = async (userId, date) => {
        try {
            // Log pour vérifier les données envoyées au backend
            console.log(`[Admin] Activation abonnement envoyée : User ${userId}, Fin ${date}`);
            await api.put(`/admin/users/${userId}`, { 
                subscription_status: 'active',
                subscription_ends_at: date 
            });
            setUsers(prevUsers =>
                prevUsers.map(user =>
                    user._id === userId ? { ...user, subscription_status: 'active', subscription_ends_at: date } : user
                )
            );
            toast.success(`Abonnement mis à jour jusqu'au ${new Date(date).toLocaleDateString()} !`);
        } catch (error) {
            // Simulation pour le mode démo
            setUsers(prevUsers =>
                prevUsers.map(user =>
                    user._id === userId ? { ...user, subscription_status: 'active', subscription_ends_at: date } : user
                )
            );
            toast.info(`Mode Démo: Abonnement mis à jour localement`);
        }
        setIsSubscriptionDateModalOpen(false);
        setUserToActivate(null);
    };

    const handleChangeSubscriptionStatus = async (userId, newStatus) => {
        try {
            await api.put(`/admin/users/${userId}`, { subscription_status: newStatus });
            setUsers(prevUsers =>
                prevUsers.map(user =>
                    user._id === userId ? { ...user, subscription_status: newStatus } : user
                )
            );
            toast.success(`Statut mis à jour !`);
        } catch (error) {
             // Simulation pour le mode démo si l'API échoue
             setUsers(prevUsers =>
                prevUsers.map(user =>
                    user._id === userId ? { ...user, subscription_status: newStatus } : user
                )
            );
            toast.info(`Mode Démo: Statut mis à jour localement`);
        }
    };

    const openChangePlanModal = (user) => {
        setUserToEditPlan(user);
        setIsPlanModalOpen(true);
    };

    const handleConfirmChangePlan = async (newPlan) => {
        if (!userToEditPlan) return;
        const userId = userToEditPlan._id;

        try {
            await api.put(`/admin/users/${userId}`, { plan: newPlan });
            setUsers(prevUsers => prevUsers.map(u => u._id === userId ? { ...u, plan: newPlan } : u));
            toast.success(`Plan mis à jour vers ${newPlan} !`);
        } catch (error) {
             // Simulation pour le mode démo
            setUsers(prevUsers => prevUsers.map(u => u._id === userId ? { ...u, plan: newPlan } : u));
            toast.info(`Mode Démo: Plan mis à jour localement vers ${newPlan}`);
        }
        setIsPlanModalOpen(false);
        setUserToEditPlan(null);
    };

    const handleViewUser = (user) => {
        setUserToView(user);
        setIsDetailModalOpen(true);
    };

    const handleDeleteUserRequest = (user) => {
        setUserToDelete(user);
        setIsConfirmModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!userToDelete) return;
        const userId = userToDelete._id;

        try {
            await api.delete(`/admin/users/${userId}`);
            setUsers(prevUsers => prevUsers.filter(u => u._id !== userId));
            toast.success("Utilisateur supprimé");
        } catch (error) {
             // Simulation pour le mode démo
             setUsers(prevUsers => prevUsers.filter(u => u._id !== userId));
             toast.info("Mode Démo: Utilisateur supprimé localement");
        }
        setIsConfirmModalOpen(false);
        setUserToDelete(null);
    };

    const filteredUsers = useMemo(() => {
        return users.filter(user => {
            // Afficher uniquement les propriétaires (owners) car les vendeurs/managers dépendent de leur abonnement
            if (user.role !== 'owner') return false;

            const matchesSearch = (
                (user.username?.toLowerCase() || '').includes(searchTerm.toLowerCase()) || 
                (user.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                (user.shopName?.toLowerCase() || '').includes(searchTerm.toLowerCase())
            );
            const matchesPlan = filterPlan === 'all' || user.plan === filterPlan;
            const matchesStatus = filterStatus === 'all' || (filterStatus === 'active' ? user.subscription_status === 'active' : user.subscription_status !== 'active');
            
            return matchesSearch && matchesPlan && matchesStatus;
        });
    }, [users, searchTerm, filterPlan, filterStatus]);

    // Calcul des stats uniquement sur les propriétaires pour éviter les doublons de revenus/comptes
    const ownerUsers = useMemo(() => users.filter(u => u.role === 'owner'), [users]);

    const stats = {
        totalUsers: ownerUsers.length,
        activeSubscriptions: ownerUsers.filter(u => u.subscription_status === 'active').length,
        premiumUsers: ownerUsers.filter(u => u.plan === 'premium').length,
        totalRevenue: ownerUsers.reduce((acc, curr) => {
            if(curr.subscription_status !== 'active') return acc;
            
            let monthlyPrice = 0;
            if(curr.plan === 'premium') monthlyPrice = 25000;
            else if(curr.plan === 'pro') monthlyPrice = 7500;

            // Gestion du cycle annuel : (Prix x 10 mois) / 12 mois pour avoir le revenu mensuel lissé
            const cycle = curr.billingCycle || curr.billing_cycle || 'monthly';
            const revenue = cycle === 'yearly' ? (monthlyPrice * 10 / 12) : monthlyPrice;

            return acc + revenue;
        }, 0)
    };

    if (loading) {
        return <Spinner message="Chargement de l'administration..." />;
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800 flex items-center">
                        <FiShield className="mr-3 text-brand-blue" />
                        Panneau d'Administration
                    </h1>
                    <p className="text-gray-500 mt-1">Gérez vos utilisateurs, abonnements et revenus.</p>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center">
                    <div className="p-3 bg-blue-50 text-blue-600 rounded-full mr-4"><FiUsers size={24} /></div>
                    <div>
                        <p className="text-sm text-gray-500 font-bold uppercase">Utilisateurs</p>
                        <p className="text-2xl font-bold text-gray-800">{stats.totalUsers}</p>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center">
                    <div className="p-3 bg-green-50 text-green-600 rounded-full mr-4"><FiActivity size={24} /></div>
                    <div>
                        <p className="text-sm text-gray-500 font-bold uppercase">Actifs</p>
                        <p className="text-2xl font-bold text-gray-800">{stats.activeSubscriptions}</p>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center">
                    <div className="p-3 bg-purple-50 text-purple-600 rounded-full mr-4"><FiUser size={24} /></div>
                    <div>
                        <p className="text-sm text-gray-500 font-bold uppercase">Premium</p>
                        <p className="text-2xl font-bold text-gray-800">{stats.premiumUsers}</p>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center">
                    <div className="p-3 bg-yellow-50 text-yellow-600 rounded-full mr-4"><FiDollarSign size={24} /></div>
                    <div>
                        <p className="text-sm text-gray-500 font-bold uppercase">Revenus (Est.)</p>
                        <p className="text-2xl font-bold text-gray-800">{Math.round(stats.totalRevenue).toLocaleString()} F</p>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-6 flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="relative w-full md:w-96">
                    <FiSearch className="absolute top-1/2 left-3 -translate-y-1/2 text-gray-400" />
                    <input 
                        type="text" 
                        placeholder="Rechercher (nom, email, boutique)..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/50"
                    />
                </div>
                <div className="flex gap-2 w-full md:w-auto">
                    <select 
                        value={filterPlan} 
                        onChange={(e) => setFilterPlan(e.target.value)}
                        className="flex-1 md:flex-none p-2 border border-gray-200 rounded-lg focus:outline-none bg-gray-50 text-sm font-medium"
                    >
                        <option value="all">Tous les plans</option>
                        <option value="essentiel">Essentiel</option>
                        <option value="pro">Pro</option>
                        <option value="premium">Premium</option>
                    </select>
                    <select 
                        value={filterStatus} 
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="flex-1 md:flex-none p-2 border border-gray-200 rounded-lg focus:outline-none bg-gray-50 text-sm font-medium"
                    >
                        <option value="all">Tous status</option>
                        <option value="active">Actif</option>
                        <option value="inactive">Inactif</option>
                    </select>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white shadow-sm rounded-2xl border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full leading-normal">
                        <thead>
                            <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200">
                                <th className="px-6 py-4">Utilisateur / Boutique</th>
                                <th className="px-6 py-4">Contact</th>
                                <th className="px-6 py-4">Plan</th>
                                <th className="px-6 py-4 text-center">Statut</th>
                                <th className="px-6 py-4 text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filteredUsers.length > 0 ? filteredUsers.map(user => (
                                <tr key={user._id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center">
                                            <div className="h-10 w-10 rounded-full bg-brand-blue/10 text-brand-blue flex items-center justify-center font-bold text-lg mr-3">
                                                {user.username ? user.username.charAt(0).toUpperCase() : 'U'}
                                            </div>
                                            <div>
                                                <p className="font-bold text-gray-900">{user.username}</p>
                                                <p className="text-xs text-gray-500">{user.shopName || 'Sans boutique'}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-sm text-gray-600 flex items-center">
                                            <FiMail className="mr-2 text-gray-400"/> {user.email}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                            user.plan === 'premium' ? 'bg-purple-100 text-purple-800' :
                                            user.plan === 'pro' ? 'bg-blue-100 text-blue-800' :
                                            'bg-gray-100 text-gray-800'
                                        }`}>
                                            {user.plan ? user.plan.toUpperCase() : 'ESSENTIEL'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        {user.subscription_status === 'active' ? (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                <span className="w-2 h-2 mr-1.5 bg-green-500 rounded-full"></span>
                                                Actif
                                            </span>
                                        ) : user.subscription_status === 'trialing' ? (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                <span className="w-2 h-2 mr-1.5 bg-yellow-500 rounded-full"></span>
                                                Essai
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                <span className="w-2 h-2 mr-1.5 bg-red-500 rounded-full"></span>
                                                Inactif
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <div className="flex justify-center space-x-2">
                                            <button 
                                                onClick={() => handleViewUser(user)}
                                                className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                                                title="Voir détails"
                                            >
                                                <FiEye size={18}/>
                                            </button>
                                            {user.subscription_status !== 'active' ? (
                                                <button 
                                                    onClick={() => handleOpenActivationModal(user)} 
                                                    className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
                                                    title="Activer l'abonnement"
                                                >
                                                    <FiCheck size={18}/>
                                                </button>
                                            ) : (
                                                <>
                                                    <button 
                                                        onClick={() => handleOpenActivationModal(user)} 
                                                        className="p-2 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition-colors"
                                                        title="Renouveler / Prolonger"
                                                    >
                                                        <FiCalendar size={18}/>
                                                    </button>
                                                    <button 
                                                        onClick={() => handleChangeSubscriptionStatus(user._id, 'inactive')} 
                                                        className="p-2 bg-yellow-50 text-yellow-600 rounded-lg hover:bg-yellow-100 transition-colors"
                                                        title="Désactiver l'abonnement"
                                                    >
                                                        <FiX size={18}/>
                                                    </button>
                                                </>
                                            )}
                                            <button 
                                                onClick={() => openChangePlanModal(user)}
                                                className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                                                title="Modifier le plan"
                                            >
                                                <FiEdit size={18}/>
                                            </button>
                                            <button 
                                                onClick={() => handleDeleteUserRequest(user)}
                                                className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                                                title="Supprimer l'utilisateur"
                                            >
                                                <FiTrash2 size={18}/>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="5" className="px-6 py-10 text-center text-gray-500">
                                        Aucun utilisateur trouvé.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            
            <ChangePlanModal 
                isOpen={isPlanModalOpen} 
                onClose={() => setIsPlanModalOpen(false)} 
                onSave={handleConfirmChangePlan} 
                currentPlan={userToEditPlan?.plan} 
            />
            
            <UserDetailModal isOpen={isDetailModalOpen} onClose={() => setIsDetailModalOpen(false)} user={userToView} />

            <SubscriptionDateModal 
                isOpen={isSubscriptionDateModalOpen} 
                onClose={() => setIsSubscriptionDateModalOpen(false)} 
                onSave={handleConfirmActivation} 
                user={userToActivate} 
            />

            <ConfirmModal
                isOpen={isConfirmModalOpen}
                onClose={() => setIsConfirmModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Supprimer l'utilisateur"
                message={`Êtes-vous sûr de vouloir supprimer l'utilisateur "${userToDelete?.username}" ? Cette action est irréversible.`}
                confirmText="Supprimer"
                cancelText="Annuler"
            />
        </div>
    );
};

export default AdminPanel;
